import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import {
  getFiltrosSafe,
  setFiltrosSafe,
} from 'src/app/filtros-buscas-modulos/filtro-busca-imobiliario/filtros-imobiliario.storage';
import { DropdownToggleDirective } from 'src/app/shared/directives/dropdown-toggle.directive';
import { FiltroBuscaService } from '../service/filtro-busca-service/filtro-busca.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-filtro-regiao',
  standalone: true,
  imports: [CommonModule, FormsModule, DropdownToggleDirective],
  templateUrl: './filtro-regiao.component.html',
  styleUrl: './filtro-regiao.component.scss',
})
export class FiltroRegiaoComponent implements OnInit {
  private alteracaoPorBuscaSalvaOuInit = false;

  @Input() buscaSalvaRegiao: {
    addressStateID?: number;
    addressCityID?: number | number[];
    addressLocationID?: number | number[];
    neighborhoodAddressID?: number | number[];
  } | null = null;

  BASE_IMG_URL: string = environment.BASE_IMG_URL;
  @Input() temAcessoModulo: boolean = true;
  @Output() regiaoChange = new EventEmitter<number>();
  @Output() filtrosChange = new EventEmitter<any>();
  @Input() disabled: boolean = false;

  filtroEstado: any[] = [];
  defaultState: number = 0;
  exibirTodasRegioes: boolean = false;
  regiaoSelecionada: number = 0;
  locations: any[] = [];
  districts: { [key: number]: any[] } = {};
  selectedLocations: number[] = [];
  selectedDistricts: any[] = [];
  // Arrays separados para SP/RJ onde cidades e bairros estão no mesmo nível
  selectedCities: number[] = []; // idAlias = 0 → Cidades
  selectedNeighborhoods: number[] = []; // idAlias != 0 → Bairros
  openDistritos: boolean = false;
  private _buscaSalvaRegiaoPendente: any = null;

  private subscriptions: Subscription = new Subscription();

  constructor(private readonly _filtroBuscaService: FiltroBuscaService) {}

  ngOnInit() {
    this.filtroRegiao();
  }

  ngOnChanges(changes: any) {
    if (
      changes['buscaSalvaRegiao'] &&
      changes['buscaSalvaRegiao'].currentValue
    ) {
      this.alteracaoPorBuscaSalvaOuInit = true;
      const busca = changes['buscaSalvaRegiao'].currentValue;
      const cityArr =
        typeof busca.addressCityID === 'string'
          ? busca.addressCityID
              .split(',')
              .map(Number)
              .filter((v: number) => !isNaN(v))
          : Array.isArray(busca.addressCityID)
            ? busca.addressCityID.map(Number)
            : [];
      const districtArr =
        typeof busca.addressLocationID === 'string'
          ? busca.addressLocationID
              .split(',')
              .map(Number)
              .filter((v: number) => !isNaN(v))
          : Array.isArray(busca.addressLocationID)
            ? busca.addressLocationID.map(Number)
            : [];
      if (
        busca.addressStateID !== this.regiaoSelecionada ||
        !this.arraysIguais(cityArr, this.selectedDistricts) ||
        !this.arraysIguais(districtArr, this.selectedLocations)
      ) {
        this.aplicarBuscaSalvaRegiao(busca);
      }
    }
  }

  arraysIguais(a: any, b: any): boolean {
    const arrA = Array.isArray(a)
      ? a
      : String(a ?? '')
          .split(',')
          .map(Number);
    const arrB = Array.isArray(b)
      ? b
      : String(b ?? '')
          .split(',')
          .map(Number);
    if (arrA.length !== arrB.length) return false;
    return arrA.every((v, i) => v === arrB[i]);
  }

  aplicarBuscaSalvaRegiao(busca: {
    addressStateID?: number;
    addressCityID?: number | number[];
    addressLocationID?: number | number[];
  }) {
    if (!this.filtroEstado.length) {
      this._buscaSalvaRegiaoPendente = busca;
      return;
    }

    if (busca.addressStateID) {
      this.regiaoSelecionada = Number(busca.addressStateID);
      const selectedRegion = this.filtroEstado.find(
        (region) => Number(region.id) === Number(busca.addressStateID),
      );
      if (selectedRegion) {
        this.locations = selectedRegion.locations || [];
        this.locations.forEach((location: any) => {
          this.districts[location.id] = location.districts || [];
        });
      }
    }

    const cityID = busca.addressCityID;
    const locationID = Array.isArray(busca.addressLocationID)
      ? (busca.addressLocationID.filter((v) => Number(v) > 0).map(Number)[0] ??
        0)
      : Number(busca.addressLocationID) || 0;
    const neighborhoodID = (busca as any).neighborhoodAddressID;

    // Verificar se há bairros selecionados (pode ser array ou string)
    const hasNeighborhoods = Array.isArray(neighborhoodID)
      ? neighborhoodID.length > 0 && neighborhoodID.some((v) => Number(v) > 0)
      : neighborhoodID && neighborhoodID !== '0' && Number(neighborhoodID) > 0;

    if (locationID > 0 || hasNeighborhoods) {
      // SP/RJ: addressLocationID = location pai (ABCD, etc), addressCityID = cidade (idAlias=0), neighborhoodAddressID = bairro (idAlias!=0)
      const locIds = Array.isArray(busca.addressLocationID)
        ? busca.addressLocationID.map(Number).filter((v) => !isNaN(v) && v > 0)
        : String(busca.addressLocationID ?? '')
            .split(',')
            .map(Number)
            .filter((v) => !isNaN(v) && v > 0);
      this.selectedLocations = locIds;

      const distIds = Array.isArray(cityID)
        ? cityID.map(Number).filter((v) => !isNaN(v) && v > 0)
        : String(cityID ?? '')
            .split(',')
            .map(Number)
            .filter((v) => !isNaN(v) && v > 0);

      const neighIds = Array.isArray(neighborhoodID)
        ? neighborhoodID.map(Number).filter((v) => !isNaN(v) && v > 0)
        : String(neighborhoodID ?? '')
            .split(',')
            .map(Number)
            .filter((v) => !isNaN(v) && v > 0);

      // Para SP/RJ: separar cidades (idAlias=0) de bairros (idAlias!=0)
      this.selectedCities = [];
      this.selectedNeighborhoods = neighIds; // Bairros vindos de neighborhoodAddressID
      this.selectedDistricts = distIds; // Mantém para compatibilidade

      // Percorrer todos os districts para separar cidades por idAlias
      this.locations.forEach((location: any) => {
        if (location.districts && Array.isArray(location.districts)) {
          location.districts.forEach((district: any) => {
            const dIds = String(district.id).split(',').map((s: string) => Number(s.trim())).filter((n: number) => !isNaN(n) && n > 0);
            const isSelected = dIds.some((dId: number) => distIds.includes(dId));
            if (isSelected && district.idAlias === 0) {
              // Cidade (idAlias=0)
              dIds.forEach((id: number) => {
                if (!this.selectedCities.includes(id)) {
                  this.selectedCities.push(id);
                }
              });
            }
          });
        }
      });
    } else {
      const cidades = Array.isArray(cityID)
        ? cityID.map(Number).filter((v) => !isNaN(v) && v > 0)
        : String(cityID ?? '')
            .split(',')
            .map(Number)
            .filter((v) => !isNaN(v) && v > 0);

      const stateHasLocationHierarchy = this.locations.some(
        (loc: any) => loc.districts && loc.districts.length > 0,
      );

      // Limpar arrays SP/RJ
      this.selectedCities = [];
      this.selectedNeighborhoods = [];

      if (stateHasLocationHierarchy) {
        // Estado com hierarquia (ex: RJ): separar locations de districts
        const locationIdSet = new Set(
          this.locations.map((loc: any) => Number(loc.id)),
        );
        this.selectedLocations = cidades.filter((id) => locationIdSet.has(id));
        this.selectedDistricts = cidades.filter((id) => !locationIdSet.has(id));
      } else {
        // Estados normais: addressCityID = cidade direta, sem location pai
        this.selectedLocations = [];
        this.selectedDistricts = cidades;
      }
    }

    if (this.locations.length > 0) {
      this.locations.forEach((location: any) => {
        const locId = Number(location.id);

        // Verificar se algum distrito está selecionado (considerando SP/RJ mode)
        const algumDistritoSelecionado =
          location.districts?.some(
            (d: any) => {
              const dIds = String(d.id).split(',').map((s: string) => Number(s.trim())).filter((n: number) => !isNaN(n) && n > 0);
              const isSPRJMode = this.selectedCities.length > 0 || this.selectedNeighborhoods.length > 0;
              
              if (isSPRJMode) {
                // SP/RJ mode
                return dIds.some((dId: number) =>
                  this.selectedCities.includes(dId) ||
                  this.selectedNeighborhoods.includes(dId) ||
                  this.selectedLocations.includes(dId),
                );
              } else {
                // Normal mode
                return dIds.some((dId: number) =>
                  this.selectedLocations.includes(dId) ||
                  this.selectedDistricts.includes(dId),
                );
              }
            },
          ) ?? false;

        location.isDropdownOpen = algumDistritoSelecionado;
        location.checked =
          this.selectedLocations.includes(locId) ||
          this.selectedDistricts.includes(locId) ||
          algumDistritoSelecionado;

        if (location.districts && Array.isArray(location.districts)) {
          location.districts.forEach((district: any) => {
            const dIds = String(district.id).split(',').map((s: string) => Number(s.trim())).filter((n: number) => !isNaN(n) && n > 0);
            const isSPRJMode = this.selectedCities.length > 0 || this.selectedNeighborhoods.length > 0;
            
            if (isSPRJMode) {
              // SP/RJ mode
              district.checked = dIds.some((dId: number) =>
                this.selectedCities.includes(dId) ||
                this.selectedNeighborhoods.includes(dId) ||
                this.selectedLocations.includes(dId),
              );
            } else {
              // Normal mode
              district.checked = dIds.some((dId: number) =>
                this.selectedLocations.includes(dId) ||
                this.selectedDistricts.includes(dId),
              );
            }
          });
        }
      });
    }

    this.alteracaoPorBuscaSalvaOuInit = false;
  }
  onSelectChange(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = Number(selectElement.value);

    this.alteracaoPorBuscaSalvaOuInit = false;

    this.regiaoSelecionada = selectedValue;

    let filtros = getFiltrosSafe();
    filtros = Array.isArray(filtros) ? filtros : [];
    filtros = filtros.filter(
      (f: any) =>
        !['Região', 'Cidades', 'Localidade', 'Distritos'].includes(
          f.filtroGrupo,
        ),
    );

    filtros.push({
      filtroGrupo: 'Região',
      filtroID: selectedValue,
      checked: true,
      description: 'Região',
    });
    setFiltrosSafe(filtros);

    this.selectedDistricts = [];
    this.selectedLocations = [];
    this.selectedCities = [];
    this.selectedNeighborhoods = [];
    if (this.locations.length > 0) {
      this.locations.forEach((location) => {
        location.isDropdownOpen = false;
      });
    }

    this.filtrosChange.emit({
      regiao: selectedValue,
      addressCityID: [],
      addressLocationID: [],
    });

    const selectedRegion = this.filtroEstado.find(
      (region) => region.id === selectedValue,
    );
    this.locations = [];
    if (selectedRegion) {
      this.locations = selectedRegion.locations || [];
      this.locations.forEach((location) => {
        this.districts[location.id] = location.districts || [];
      });
    }
  }

  restaurarPadrao(): void {
    this.alteracaoPorBuscaSalvaOuInit = false;
    this.regiaoSelecionada = this.defaultState;

    this.selectedDistricts = [];
    this.selectedLocations = [];
    this.selectedCities = [];
    this.selectedNeighborhoods = [];

    this.locations.forEach((location: any) => {
      location.isDropdownOpen = false;
      (location.districts ?? []).forEach((district: any) => (district.checked = false));
    });

    const selectedRegion = this.filtroEstado.find(
      (region) => Number(region.id) === Number(this.defaultState),
    );
    this.locations = selectedRegion?.locations ?? [];
    this.locations.forEach((location: any) => {
      this.districts[location.id] = location.districts || [];
    });

    const filtros = getFiltrosSafe().filter(
      (f: any) =>
        !['Região', 'Cidades', 'Localidade', 'Distritos', 'Bairros'].includes(
          f.filtroGrupo,
        ),
    );

    if (this.defaultState) {
      filtros.push({
        filtroGrupo: 'Região',
        filtroID: this.defaultState,
        checked: true,
        description: 'Região',
      });
    }

    setFiltrosSafe(filtros);

    this.filtrosChange.emit({
      regiao: this.defaultState,
      addressCityID: [],
      addressLocationID: [],
      neighborhoodAddressID: [],
    });
  }

  isLocationSelected(id: number): boolean {
    return this.selectedLocations?.includes(id);
  }

  isDistrictChecked(district: any): boolean {
    const ids = String(district.id)
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);

    // Para SP/RJ: verificar em selectedCities (idAlias=0) ou selectedNeighborhoods (idAlias!=0)
    // Para outros estados: verificar em selectedDistricts
    if (this.selectedCities.length > 0 || this.selectedNeighborhoods.length > 0) {
      // SP/RJ mode
      if (district.idAlias === 0) {
        return ids.some((id) => this.selectedCities.includes(id));
      } else {
        return ids.some((id) => this.selectedNeighborhoods.includes(id));
      }
    } else {
      // Normal mode
      return ids.some((id) => this.selectedDistricts.includes(id));
    }
  }

  onLocationCheckboxChange(location: any, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const checked = checkbox.checked;
    const locationId = Number(location.id);

    const stateHasLocationHierarchy = this.locations.some(
      (loc: any) => loc.districts && loc.districts.length > 0,
    );

    if (checked) {
      if (stateHasLocationHierarchy) {
        if (!this.selectedLocations.includes(locationId)) {
          this.selectedLocations.push(locationId);
        }
      } else {
        if (!this.selectedDistricts.includes(locationId)) {
          this.selectedDistricts.push(locationId);
        }
      }
    } else {
      this.selectedLocations = this.selectedLocations.filter(
        (id) => id !== locationId,
      );
      this.selectedDistricts = this.selectedDistricts.filter(
        (id) => id !== locationId,
      );
    }

    let filtros = getFiltrosSafe();
    filtros = filtros.filter(
      (f: any) => !['Cidades', 'Distritos', 'Localidade', 'Bairros'].includes(f.filtroGrupo),
    );

    // Para SP/RJ: usar selectedCities e selectedNeighborhoods
    // Para outros estados: usar selectedDistricts
    if (this.selectedCities.length > 0 || this.selectedNeighborhoods.length > 0) {
      // SP/RJ mode
      this.selectedCities.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Cidades',
          filtroID: id,
          checked: true,
          description: 'Cidade',
        });
      });
      this.selectedNeighborhoods.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Bairros',
          filtroID: id,
          checked: true,
          description: 'Bairro',
        });
      });
      this.selectedLocations.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Localidade',
          filtroID: id,
          checked: true,
          description: 'Localidade',
        });
      });
    } else {
      // Normal mode
      this.selectedDistricts.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Distritos',
          filtroID: id,
          checked: true,
          description: 'Distrito',
        });
      });
      this.selectedLocations.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Cidades',
          filtroID: id,
          checked: true,
          description: 'Cidade',
        });
      });
    }
    setFiltrosSafe(filtros);

    this.alteracaoPorBuscaSalvaOuInit = false;
    this.emitFiltrosAtualizados();
  }

  toggleDropdownIcon(location: any): void {
    this.locations.forEach((loc) => {
      if (loc !== location) {
        loc.isDropdownOpen = false;
      }
    });
    location.isDropdownOpen = !location.isDropdownOpen;
  }

  onDistrictCheckboxChange(district: any, event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const checked = checkbox.checked;

    // district.id pode ser comma-separated (ex: "25247,26269")
    const districtIds = String(district.id)
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => !isNaN(n) && n > 0);

    // Encontrar o location pai deste district
    // district.id pode ser comma-separated, verificar se algum ID corresponde
    const parentLocation = this.locations.find((loc: any) =>
      loc.districts?.some((d: any) => {
        const dIds = String(d.id).split(',').map((s: string) => Number(s.trim())).filter((n: number) => !isNaN(n) && n > 0);
        return dIds.some((dId: number) => districtIds.includes(dId));
      }),
    );
    const parentLocationId = parentLocation ? Number(parentLocation.id) : 0;

    if (checked) {
      // idAlias = 0 → Cidade (vai para selectedCities → addressCityID)
      // idAlias != 0 → Bairro (vai para selectedNeighborhoods → addressLocationID)
      if (district.idAlias === 0) {
        // Cidade
        districtIds.forEach((id) => {
          if (!this.selectedCities.includes(id)) {
            this.selectedCities.push(id);
          }
        });
      } else {
        // Bairro - IDs vão para selectedNeighborhoods
        districtIds.forEach((id) => {
          if (!this.selectedNeighborhoods.includes(id)) {
            this.selectedNeighborhoods.push(id);
          }
        });
        // Se idAlias !== 0, o location PAI também vai para selectedLocations
        if (parentLocationId > 0 && !this.selectedLocations.includes(parentLocationId)) {
          this.selectedLocations.push(parentLocationId);
        }
      }
    } else {
      if (district.idAlias === 0) {
        // Remover cidade de selectedCities
        this.selectedCities = this.selectedCities.filter(
          (id) => !districtIds.includes(id),
        );
      } else {
        // Remover bairro de selectedNeighborhoods
        this.selectedNeighborhoods = this.selectedNeighborhoods.filter(
          (id) => !districtIds.includes(id),
        );
        // Remover location pai se nenhum outro bairro do mesmo pai está selecionado
        if (parentLocation && parentLocationId > 0) {
          const outroBairroSelecionado = parentLocation.districts?.some(
            (d: any) =>
              d.id !== district.id &&
              d.idAlias !== 0 &&
              String(d.id)
                .split(',')
                .some((s: string) =>
                  this.selectedNeighborhoods.includes(Number(s.trim())),
                ),
          );
          if (!outroBairroSelecionado) {
            this.selectedLocations = this.selectedLocations.filter(
              (id) => id !== parentLocationId,
            );
          }
        }
      }
    }

    let filtros = getFiltrosSafe();
    filtros = filtros.filter(
      (f: any) => !['Cidades', 'Distritos', 'Localidade', 'Bairros'].includes(f.filtroGrupo),
    );

    // Para SP/RJ: usar selectedCities e selectedNeighborhoods
    // Para outros estados: usar selectedDistricts
    if (this.selectedCities.length > 0 || this.selectedNeighborhoods.length > 0) {
      // SP/RJ mode
      this.selectedCities.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Cidades',
          filtroID: id,
          checked: true,
          description: 'Cidade',
        });
      });
      this.selectedNeighborhoods.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Bairros',
          filtroID: id,
          checked: true,
          description: 'Bairro',
        });
      });
      this.selectedLocations.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Localidade',
          filtroID: id,
          checked: true,
          description: 'Localidade',
        });
      });
    } else {
      // Normal mode
      this.selectedDistricts.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Distritos',
          filtroID: id,
          checked: true,
          description: 'Distrito',
        });
      });
      this.selectedLocations.forEach((id) => {
        filtros.push({
          filtroGrupo: 'Cidades',
          filtroID: id,
          checked: true,
          description: 'Cidade',
        });
      });
    }

    setFiltrosSafe(filtros);

    this.alteracaoPorBuscaSalvaOuInit = false;
    this.emitFiltrosAtualizados();
  }

  private emitFiltrosAtualizados(): void {
    if (this.alteracaoPorBuscaSalvaOuInit) return;

    // Para SP/RJ: selectedCities (idAlias=0) → addressCityID, selectedNeighborhoods (idAlias!=0) → neighborhoodAddressID
    // Para outros estados: selectedDistricts → addressCityID, selectedLocations → addressLocationID
    const hasCitiesOrNeighborhoods = this.selectedCities.length > 0 || this.selectedNeighborhoods.length > 0;

    const addressCityIDs = hasCitiesOrNeighborhoods
      ? Array.from(new Set(this.selectedCities)).filter((id) => !isNaN(id) && id > 0)
      : Array.from(new Set(this.selectedDistricts)).filter((id) => !isNaN(id) && id > 0);

    const addressLocationIDs = hasCitiesOrNeighborhoods
      ? Array.from(new Set(this.selectedLocations)).filter((id) => !isNaN(id) && id > 0)
      : Array.from(new Set(this.selectedLocations)).filter((id) => !isNaN(id) && id > 0);

    const neighborhoodAddressIDs = hasCitiesOrNeighborhoods
      ? Array.from(new Set(this.selectedNeighborhoods)).filter((id) => !isNaN(id) && id > 0)
      : [];

    const regiao =
      this.regiaoSelecionada !== 0 ? this.regiaoSelecionada : this.defaultState;

    if (regiao === 0) return;

    const payload = {
      regiao,
      addressCityID: addressCityIDs,
      addressLocationID: addressLocationIDs,
      neighborhoodAddressID: neighborhoodAddressIDs,
    };

    this.filtrosChange.emit(payload);
  }

  filtroRegiao() {
    this.subscriptions.add(
      this._filtroBuscaService.buscarFiltroEstados().subscribe({
        next: (res) => {
          if (res.success) {
            this.filtroEstado = res.data.states;
            this.defaultState = res.data.defaultState;

            if (this.defaultState === 0) {
              this.exibirTodasRegioes = true;
            } else {
              this.exibirTodasRegioes = false;
            }

            this.regiaoSelecionada = this.defaultState;

            if (this._buscaSalvaRegiaoPendente) {
              this.aplicarBuscaSalvaRegiao(this._buscaSalvaRegiaoPendente);
              this._buscaSalvaRegiaoPendente = null;
              this.emitFiltrosAtualizados();
              return;
            }

            if (this.filtroEstado.length === 1 && this.defaultState) {
              this.onSelectChange({
                target: { value: this.defaultState },
              } as any as Event);
            } else if (this.defaultState !== 0) {
              let filtros = getFiltrosSafe();
              filtros = filtros.filter(
                (f: any) =>
                  !['Região', 'Cidades', 'Distritos'].includes(f.filtroGrupo),
              );
              filtros.push({
                filtroGrupo: 'Região',
                filtroID: this.defaultState,
                checked: true,
                description: 'Região',
              });
              setFiltrosSafe(filtros);

              const selectedRegion = this.filtroEstado.find(
                (r) => Number(r.id) === Number(this.defaultState),
              );
              if (selectedRegion) {
                this.locations = selectedRegion.locations || [];
                this.locations.forEach((location: any) => {
                  this.districts[location.id] = location.districts || [];
                });
              }

              this.filtrosChange.emit({
                regiao: this.defaultState,
                addressCityID: [],
                addressLocationID: [],
              });
            } else {
              this.filtrosChange.emit({
                regiao: 0,
                addressCityID: [],
                addressLocationID: [],
              });
            }
          }
        },
      }),
    );
  }
}
